import { useState, useEffect, useRef } from 'react'
import { Menu, X, ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Logo from './Logo'
import LanguageSwitcher from './LanguageSwitcher'
import { useLanguage } from '../contexts/LanguageContext'

const Navbar = () => {
  const { t } = useLanguage()
  const location = useLocation()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false)
  const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(false)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const featuresDropdownRef = useRef<HTMLDivElement>(null)

  const featurePages = [
    { name: t('nav.features.dispoPrevent'), path: '/features/dispo-prevention' },
    { name: t('nav.features.charts'), path: '/features/charts-analytics' },
    { name: t('nav.features.aiTips'), path: '/features/ai-tips' },
    { name: t('nav.features.roastMode'), path: '/features/roast-mode' },
    { name: t('nav.features.habitsScore'), path: '/features/habits-score' },
    { name: t('nav.features.checkIn'), path: '/features/check-in' },
    { name: t('nav.features.points'), path: '/features/azrefy-points' },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const scrollToSection = (id: string) => {
    setIsOpen(false)
    
    // If we're not on the homepage, navigate there first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } })
      return
    }
    
    // Small delay to allow menu to close before scrolling
    setTimeout(() => {
      const element = document.getElementById(id)
      if (element) {
        const offset = 80 // Account for fixed navbar height
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - offset
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      }
    }, 100)
  }

  return (
    <nav
      ref={mobileMenuRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-black/95 backdrop-blur-xl shadow-lg border-b border-white/10 py-3'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Logo />

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <button
              onClick={() => scrollToSection('dispo-escape')}
              className="text-gray-300 hover:text-white font-medium transition-colors text-sm lg:text-base"
            >
              {t('nav.dispoEscape')}
            </button>
            
            <button
              onClick={() => scrollToSection('features')}
              className="text-gray-300 hover:text-white font-medium transition-colors text-sm lg:text-base"
            >
              {t('nav.whyAzrefy')}
            </button>
            
            {/* Features Dropdown */}
            <div 
              className="relative"
              ref={featuresDropdownRef}
              onMouseEnter={() => setIsFeaturesOpen(true)}
              onMouseLeave={() => setIsFeaturesOpen(false)}
            >
              <button
                onClick={() => scrollToSection('app-showcase')}
                className="text-gray-300 hover:text-white font-medium transition-colors text-sm lg:text-base flex items-center gap-1"
              >
                {t('nav.powerfulFeatures')}
                <ChevronDown size={16} className={`transition-transform ${isFeaturesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              <AnimatePresence>
                {isFeaturesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-black/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                  >
                    {featurePages.map((feature, index) => {
                      const isActive = location.pathname === feature.path
                      return (
                        <Link
                          key={feature.path}
                          to={feature.path}
                          className={`block px-4 py-3 transition-all text-sm border-b border-white/5 last:border-b-0 ${
                            isActive
                              ? 'text-blue-400 bg-blue-500/10 font-semibold'
                              : 'text-gray-300 hover:text-white hover:bg-white/5'
                          }`}
                          onClick={() => setIsFeaturesOpen(false)}
                        >
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            {feature.name}
                          </motion.div>
                        </Link>
                      )
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="text-gray-300 hover:text-white font-medium transition-colors text-sm lg:text-base"
            >
              {t('nav.howItWorks')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-300 hover:text-white font-medium transition-colors text-sm lg:text-base"
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-300 hover:text-white font-medium transition-colors text-sm lg:text-base"
            >
              {t('nav.contact')}
            </button>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 hover:bg-gray-900 rounded-lg transition-colors text-white"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="flex flex-col gap-4 py-6 border-t border-gray-800 mt-4 bg-black/95 backdrop-blur-xl rounded-2xl px-4">
                {/* Mobile Menu Logo - Innovative centerpiece */}
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className="flex justify-center mb-4"
                >
                  <div className="relative">
                    {/* Glow effect behind logo */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 blur-2xl opacity-30 rounded-full"></div>
                    <Logo variant="full" className="h-24 relative z-10" />
                  </div>
                </motion.div>
                
                <button
                  onClick={() => scrollToSection('dispo-escape')}
                  className="text-left text-white font-medium py-3 px-2 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-all"
                >
                  {t('nav.dispoEscape')}
                </button>
                
                <button
                  onClick={() => scrollToSection('features')}
                  className="text-left text-white font-medium py-3 px-2 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-all"
                >
                  {t('nav.whyAzrefy')}
                </button>
                
                {/* Mobile Features Expandable */}
                <div>
                  <button
                    onClick={() => setIsMobileFeaturesOpen(!isMobileFeaturesOpen)}
                    className="w-full text-left text-white font-medium py-3 px-2 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-all flex items-center justify-between"
                  >
                    <span>{t('nav.powerfulFeatures')}</span>
                    <ChevronDown size={20} className={`transition-transform ${isMobileFeaturesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Expandable Feature Links */}
                  <AnimatePresence>
                    {isMobileFeaturesOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden ml-4"
                      >
                        {featurePages.map((feature) => {
                          const isActive = location.pathname === feature.path
                          return (
                            <Link
                              key={feature.path}
                              to={feature.path}
                              onClick={() => {
                                setIsOpen(false)
                                setIsMobileFeaturesOpen(false)
                              }}
                              className={`block py-2 px-2 rounded-lg transition-all text-sm ${
                                isActive
                                  ? 'text-blue-400 bg-blue-500/10 font-semibold'
                                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                              }`}
                            >
                              {feature.name}
                            </Link>
                          )
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                
                <button
                  onClick={() => scrollToSection('how-it-works')}
                  className="text-left text-white font-medium py-3 px-2 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-all"
                >
                  {t('nav.howItWorks')}
                </button>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-left text-white font-medium py-3 px-2 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-all"
                >
                  {t('nav.about')}
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-left text-white font-medium py-3 px-2 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-all"
                >
                  {t('nav.contact')}
                </button>
                <div className="py-2 px-2">
                  <LanguageSwitcher />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  )
}

export default Navbar
